import React from 'react';
import { CheckCircle, Calendar, Target, TrendingUp } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import StaticDragButton from '@/components/ui/StaticDragButton';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { Goal } from './GoalCard';
import { useTasks } from '@/contexts/task/TaskContext';
import { getProgressColors } from '@/utils/goal-progress-colors';

interface GoalGhostProps {
  goal: Goal;
}

/**
 * Enhanced ghost component for goal drag overlay
 * Matches the actual GoalCard design with drag button and glass morphism
 */
const GoalGhost: React.FC<GoalGhostProps> = ({ goal }) => {
  const { id, title, progress: dbProgress, dueDate, projectTitle, projectCoverImage } = goal;
  const isMobile = useIsMobile();
  
  // Get global tasks to calculate real progress (same as GoalCard)
  const { activeTasks, completedTasks } = useTasks();
  
  // Filter tasks for this goal and calculate progress
  const goalActiveTasks = activeTasks.filter(task => task.goalId === id);
  const goalCompletedTasks = completedTasks.filter(task => task.goalId === id);
  const totalTasks = goalActiveTasks.length + goalCompletedTasks.length;
  const completedTasksCount = goalCompletedTasks.length;
  
  // Calculate progress based on task completion (single source of truth)
  const calculatedProgress = totalTasks > 0 ? Math.round((completedTasksCount / totalTasks) * 100) : 0;
  
  // Use calculated progress as the primary source, fall back to database progress if no tasks
  const displayProgress = totalTasks > 0 ? calculatedProgress : dbProgress;

  // Get dynamic colors based on progress
  const progressColors = getProgressColors(displayProgress);

  // Format due date (same as GoalCard)
  const formatDueDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} days`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else if (diffDays <= 7) {
      return `Due in ${diffDays} days`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const isOverdue = dueDate && new Date(dueDate) < new Date();
  const isCompleted = displayProgress === 100;

  return (
    <div
      className="group relative rounded-lg overflow-hidden h-full flex flex-col cursor-pointer shadow-lg"
      style={{ 
        borderRadius: '0.5rem',
        transform: 'translateZ(0) rotate(1deg)',
        backfaceVisibility: 'hidden',
        willChange: 'transform, box-shadow',
        transformStyle: 'preserve-3d',
        WebkitFontSmoothing: 'antialiased',
        ...(projectCoverImage ? { aspectRatio: '16/9' } : {})
      }}
    >
      {/* Background Image (if provided) */}
      {projectCoverImage && (
        <div 
          className="absolute inset-0 w-full h-full z-0"
          style={{
            backgroundImage: `url(${projectCoverImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: 'inherit'
          }}
        />
      )}
      
      {/* Dark overlay for text readability when using background image */}
      {projectCoverImage && (
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/40 to-black/20 z-0" style={{ borderRadius: 'inherit' }} />
      )}
      
      {/* Glass morphism background - matching MovableCard */}
      <div 
        className={cn(
          "absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 will-change-transform",
          projectCoverImage 
            ? "bg-gradient-to-br from-black/40 via-black/20 to-black/10" 
            : "bg-gradient-to-br from-white/70 via-white/40 to-white/20 dark:from-black/40 dark:to-black/10"
        )}
        style={{ 
          transform: 'translateZ(0)',
          borderRadius: 'inherit'
        }}
      />
      
      {/* Enhanced border for visibility during drag */}
      <div className="absolute inset-0 rounded-lg border border-white/40 dark:border-white/20 -z-10" />

      {/* Drag button in top right corner - beautiful static active state */}
      <div className="absolute top-3 right-3 z-10">
        <StaticDragButton
          variant="glass"
          size="sm"
          iconType="vertical"
          className="opacity-95"
        />
      </div>

      {/* Goal Content with proper padding - matching GoalCard */}
      <div 
        className={`${isMobile ? 'p-3 pb-4' : 'p-5 pb-6'} flex flex-col h-full relative z-1`} 
        style={{ 
          willChange: 'contents',
          borderRadius: 'inherit'
        }}
      >
        {/* Goal Content - with right padding to avoid overlap with move button */}
        <div className="flex flex-col justify-between h-full space-y-5">
          {/* Header Section */}
          <div>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200",
                    isCompleted 
                      ? "bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/25" 
                      : `bg-gradient-to-br ${progressColors.cardGradient} shadow-lg ${progressColors.shadowClass}`
                  )}>
                    {isCompleted ? (
                      <CheckCircle className="text-white" size={20} />
                    ) : (
                      <Target className="text-white" size={20} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Project Title (if available) */}
                    {projectTitle && (
                      <h4 className="text-xs font-medium text-gray-300 dark:text-gray-300 mb-0.5">
                        {projectTitle}
                      </h4>
                    )}
                    
                    <h3 className={cn(
                      "font-semibold text-lg leading-tight transition-colors duration-200",
                      isCompleted 
                        ? "text-green-300 dark:text-green-300 line-through decoration-2" 
                        : projectCoverImage ? "text-white dark:text-white" : "text-gray-900 dark:text-white"
                    )}>
                      {title}
                    </h3>
                    {totalTasks > 0 && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className={cn(
                          "text-sm",
                          projectCoverImage ? "text-gray-300 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"
                        )}>
                          {completedTasksCount} of {totalTasks} tasks completed
                        </span>
                        {!isCompleted && completedTasksCount > 0 && (
                          <TrendingUp className={cn(
                            "transition-colors duration-200",
                            projectCoverImage ? "text-white" : progressColors.cardColor
                          )} size={14} />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Progress Section - Now at the bottom */}
          <div className="space-y-3 mt-auto">
            <div className="flex items-center justify-between">
              <span className={cn(
                "text-sm font-medium",
                projectCoverImage ? "text-gray-300 dark:text-gray-300" : "text-gray-600 dark:text-gray-300"
              )}>
                Progress
              </span>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-sm font-bold transition-colors duration-200",
                  isCompleted 
                    ? "text-green-300 dark:text-green-300" 
                    : projectCoverImage ? "text-white dark:text-white" : "text-gray-900 dark:text-white"
                )}>
                  {displayProgress}%
                </span>
                {displayProgress > 0 && displayProgress < 100 && (
                  <div className={cn(
                    "w-2 h-2 rounded-full animate-pulse",
                    `bg-gradient-to-r ${progressColors.progressGradient}`
                  )} />
                )}
              </div>
            </div>
            
            <div className="relative">
              <Progress 
                value={displayProgress} 
                className="h-3 bg-gray-200/60 dark:bg-gray-700/60 rounded-full overflow-hidden shadow-inner" 
                indicatorClassName={cn(
                  `bg-gradient-to-r ${progressColors.progressGradient} transition-all duration-500 ease-out`,
                  displayProgress > 0 && "shadow-lg"
                )}
              />
              {/* Subtle glow effect for higher progress */}
              {displayProgress > 50 && (
                <div className={cn(
                  "absolute inset-0 rounded-full opacity-20 blur-sm",
                  `bg-gradient-to-r ${progressColors.progressGradient}`
                )} />
              )}
            </div>
          </div>
          
          {/* Due Date Section - Moved above progress section */}
          {dueDate && (
            <div className={cn(
              "flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors duration-200",
              isOverdue && !isCompleted
                ? "bg-red-500/20 dark:bg-red-950/40 text-red-100 dark:text-red-300 border border-red-500/30 dark:border-red-800/30"
                : isCompleted
                ? "bg-green-500/20 dark:bg-green-950/40 text-green-100 dark:text-green-300 border border-green-500/30 dark:border-green-800/30"
                : "bg-blue-500/20 dark:bg-blue-950/40 text-blue-100 dark:text-blue-300 border border-blue-500/30 dark:border-blue-800/30"
            )}>
              <Calendar size={14} className="flex-shrink-0" />
              <span className="font-medium">
                {formatDueDate(dueDate)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoalGhost; 